import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { normalizeRole } from "../utils/rbacHelper";

export const DASHBOARD_TAB_VALUES = Object.freeze([
  "assignments",
  "statics",
  "map",
  "history_playback",
  "history_logs",
  "history_events",
  "history_parking",
  "fuel_monitoring",
  "white_label",
  "users",
  "devices",
  "alarms",
  "events",
  "live_camera",
  "task_management",
  "my_tasks",
]);

const DEFAULT_DASHBOARD_TAB = "assignments";
const DASHBOARD_NAV_STORAGE_PREFIX = "gps:dashboard-nav:";
export const LIVE_MAP_PATH = "/live-map";
const DASHBOARD_PATH = "/dashboard";
const LEGACY_DASHBOARD_TAB_TO_PATH = Object.freeze({
  assignments: "/dashboard/assignments",
  statics: "/dashboard/dashboard",
  map: "/dashboard/map",
  history_playback: "/dashboard/history/playback",
  history_logs: "/dashboard/history/logs",
  history_events: "/dashboard/history/events",
  history_parking: "/dashboard/history/parking",
  fuel_monitoring: "/dashboard/fuel-monitoring",
  white_label: "/dashboard/white-label",
  users: "/dashboard/users",
  devices: "/dashboard/devices",
  alarms: "/dashboard/alarms",
  events: "/dashboard/events",
  live_camera: "/dashboard/live-camera",
  task_management: "/dashboard/task-management",
  my_tasks: "/dashboard/my-tasks",
});
const COMPATIBILITY_PATH_TO_TAB = Object.freeze({
  "/vehicles": "devices",
  "/history/playback": "history_playback",
  "/history/logs": "history_logs",
  "/history/events": "history_events",
  "/history/parking": "history_parking",
  [LIVE_MAP_PATH]: "map",
  ...Object.fromEntries(
    Object.entries(LEGACY_DASHBOARD_TAB_TO_PATH).map(([tab, path]) => [path, tab])
  ),
});

export const isDashboardShellPath = (pathname = "") => {
  const value = String(pathname || "").replace(/\/+$/g, "");
  return (
    value === DASHBOARD_PATH ||
    value === LIVE_MAP_PATH ||
    Boolean(COMPATIBILITY_PATH_TO_TAB[value]) ||
    value.startsWith("/admin-dashboard")
  );
};

export const normalizeDashboardTab = (value, fallback = DEFAULT_DASHBOARD_TAB) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return DASHBOARD_TAB_VALUES.includes(normalized) ? normalized : fallback;
};

export const readDashboardTabFromSearch = (search, fallback = DEFAULT_DASHBOARD_TAB) => {
  const params = new URLSearchParams(String(search || ""));
  return normalizeDashboardTab(params.get("tab"), fallback);
};

export const resolveDashboardPathFromTab = (value, fallback = DEFAULT_DASHBOARD_TAB) => {
  normalizeDashboardTab(value, fallback);
  return DASHBOARD_PATH;
};

export const resolveDashboardTabFromPath = (pathname, fallback = DEFAULT_DASHBOARD_TAB) => {
  const normalizedPath = String(pathname || "").replace(/\/+$/g, "");
  if (COMPATIBILITY_PATH_TO_TAB[normalizedPath]) {
    return COMPATIBILITY_PATH_TO_TAB[normalizedPath];
  }
  return fallback;
};

export const getDefaultDashboardTabForRole = (role) => {
  const normalized = normalizeRole(role);
  if (normalized === "viewer") return "map";
  if (normalized === "field_emp") return "my_tasks";
  return DEFAULT_DASHBOARD_TAB;
};

export const buildAdminDashboardPath = ({
  tab,
  mapStatusFilter = null,
  navToken = null,
} = {}) => {
  const normalizedTab = normalizeDashboardTab(tab, DEFAULT_DASHBOARD_TAB);
  const basePath = resolveDashboardPathFromTab(normalizedTab);
  const params = new URLSearchParams();

  params.set("tab", normalizedTab);

  if (mapStatusFilter) {
    params.set(
      "status",
      String(mapStatusFilter || "")
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, "_")
    );
  }

  if (navToken) params.set("nav", String(navToken));

  const query = params.toString();
  return `${basePath}${query ? `?${query}` : ""}`;
};

function makeDashboardNavToken() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export const stashDashboardNavPayload = (payload = {}) => {
  const normalizedPayload = Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined && value !== null)
  );
  if (!Object.keys(normalizedPayload).length) return null;

  const token = makeDashboardNavToken();
  try {
    sessionStorage.setItem(
      `${DASHBOARD_NAV_STORAGE_PREFIX}${token}`,
      JSON.stringify({
        createdAt: Date.now(),
        payload: normalizedPayload,
      })
    );
    return token;
  } catch (_) {
    return null;
  }
};

export const consumeDashboardNavPayload = (token) => {
  const key = `${DASHBOARD_NAV_STORAGE_PREFIX}${String(token || "").trim()}`;
  if (!String(token || "").trim()) return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    sessionStorage.removeItem(key);
    const parsed = JSON.parse(raw);
    return parsed?.payload && typeof parsed.payload === "object" ? parsed.payload : null;
  } catch (_) {
    sessionStorage.removeItem(key);
    return null;
  }
};

export const navigateToAdminDashboard = (
  navigate,
  {
    tab = DEFAULT_DASHBOARD_TAB,
    mapStatusFilter = null,
    quickAction = null,
    focusRoute = null,
    playbackFocus = null,
    replace = false,
  } = {}
) => {
  const navPayload = {};
  if (quickAction) navPayload.quickAction = quickAction;
  if (focusRoute) navPayload.focusRoute = focusRoute;
  if (playbackFocus) navPayload.playbackFocus = playbackFocus;
  const navToken = stashDashboardNavPayload(navPayload);

  navigate(
    buildAdminDashboardPath({
      tab,
      mapStatusFilter,
      navToken,
    }),
    { replace }
  );
};

export const useResolvedDashboardTab = (fallback = DEFAULT_DASHBOARD_TAB) => {
  const location = useLocation();
  return useMemo(() => {
    if (location.pathname === LIVE_MAP_PATH) return "map";
    if (location.pathname !== DASHBOARD_PATH) return fallback;
    const fromPath = resolveDashboardTabFromPath(location.pathname, null);
    if (fromPath) return fromPath;
    return readDashboardTabFromSearch(location.search, fallback);
  }, [fallback, location.pathname, location.search]);
};
